import type { Translate } from "@privacy/translations";
import type { GridColDef, GridValidRowModel } from "@mui/x-data-grid-premium";
import { Stack, Tooltip } from "@mui/material";
import { DynamicGrid } from "../dynamicGrid";
import { EmptyCell } from "../dynamicGrid/columnDefinitions";
import type { ClassificationTemplate } from "../api/classificationTemplate";
import { ClassificationScoreChip } from "./ClassificationScoreChip";
import type { Score } from "./schemas";

export const classificationScoresColumn = <Row extends GridValidRowModel>(
  t: Translate,
  template: ClassificationTemplate | undefined,
  getScores: (row: Row) => Array<Score>
): GridColDef<Row> => {
  const initials = template ? ` (${template.properties.map(p => p.label[0]).join("")})` : "";
  return {
    ...DynamicGrid.requiredColDef({
      field: "classificationScores",
      headerName: t("Classification.Grid.ChipColumnHeader", { initials }),
    }),
    width: 150,
    minWidth: 120,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    valueGetter: (_, row) => {
      const scores = getScores(row);
      if (!template || scores.length === 0) return null;
      const labelByKey = new Map(template.properties.map(p => [p.key, p.label]));
      return scores
        .flatMap(score => {
          const label = labelByKey.get(score.propertyKey);
          return label ? [`${label} ${score.value}`] : [];
        })
        .join(", ");
    },
    renderCell: ({ row }) => {
      const scores = getScores(row);
      if (!template || scores.length === 0) {
        return (
          <DynamicGrid.Cell>
            <EmptyCell />
          </DynamicGrid.Cell>
        );
      }
      const valueByKey = new Map(scores.map(s => [s.propertyKey, s.value]));
      return (
        <DynamicGrid.Cell withExtraSpacingTop={false}>
          <Stack direction="row" sx={{ gap: 0.5, alignItems: "center", justifyContent: "center" }}>
            {template.properties.map(property => {
              const value = valueByKey.get(property.key);
              if (value === undefined) return null;
              return (
                <Tooltip enterDelay={100} key={property.key} title={property.label}>
                  <span>
                    <ClassificationScoreChip propertyKey={property.key} value={value} ariaLabel={property.label} />
                  </span>
                </Tooltip>
              );
            })}
          </Stack>
        </DynamicGrid.Cell>
      );
    },
  };
};
